import type { PerimetreValue } from './PerimetreSelector';

// Region du referentiel (GET /perimetre/referentiel).
export interface ReferentielRegion {
  id: number;
  nom: string;
  code: string;
  departements: { id: number; nom: string }[];
}

export type DeptCoverage = 'region' | 'isole' | 'libre';

// Index departement -> sa region (pour resoudre un clic carte vers une region).
export function makeDeptIndex(referentiel: ReferentielRegion[]) {
  const idx = new Map<number, { region: ReferentielRegion; dept: { id: number; nom: string } }>();
  referentiel.forEach(region => region.departements.forEach(dept => idx.set(dept.id, { region, dept })));
  return idx;
}

/**
 * Construit la fonction de couverture (deptId) => 'region' | 'isole' | 'libre'
 * a partir d'une valeur de perimetre et du referentiel. 'national' couvre tous
 * les departements comme regions entieres. Source unique partagee par le
 * selecteur (edition) et l'affichage carte lecture seule.
 */
export function deptStateFromValue(
  value: PerimetreValue,
  referentiel: ReferentielRegion[],
): (deptId: number) => DeptCoverage {
  const isNational = value.type === 'national';
  const regionIds = new Set(isNational ? referentiel.map(r => r.id) : value.regions.map(r => r.id));
  const deptByRegion = new Map<number, Set<number>>();
  if (!isNational) {
    value.departements.forEach(d => {
      if (!deptByRegion.has(d.id_region)) deptByRegion.set(d.id_region, new Set());
      deptByRegion.get(d.id_region)!.add(d.id);
    });
  }
  const idx = makeDeptIndex(referentiel);
  return (deptId: number) => {
    const entry = idx.get(deptId);
    if (!entry) return 'libre';
    if (regionIds.has(entry.region.id)) return 'region';
    return deptByRegion.get(entry.region.id)?.has(deptId) ? 'isole' : 'libre';
  };
}
